import React, { useEffect, useState, useRef } from "react";
import Slider from "react-slick";
import RichText from "@qlik/qlik/src/components/RichText";
import "@qlik/qlik/src/assets/vendor/slick/slick.css";
import "@qlik/qlik/src/assets/vendor/slick/slick-theme.css";
import Cta from "@qlik/qlik/src/components/Cta";
import Media from "@qlik/qlik/src/components/Media";
import { getSpacingClassName } from "@qlik/qlik/src/lib/util/spacing";
import { renderSanitizedHtmlContent, handleKeyboardEventBehavior } from "@qlik/qlik/src/lib/util/helpers";
import * as styles from "./styles.module.css";

/**
 * HomeTabSlider component
 * @param {Object} data - The data object containing slider details
 */
const HomeTabSlider = ({ data }) => {
    // State variables for slider navigation and settings
    const [sliderTabNavigation, setSliderTabNavigation] = useState(null);
    const [sliderContentNavigation, setSliderContentNavigation] = useState(null);
    const [sliderTab, setSliderTab] = useState(null);
    const [sliderContent, setSliderContent] = useState(null);
    const [shouldAutoplay, setShouldAutoplay] = useState(false);
    const [totalSlides, setTotalSlides] = useState(3);
    const [sliderInitialized, setSliderInitialized] = useState(false);
    const [sliderKey, setSliderKey] = useState(0);
    const sectionPaddingClassName = getSpacingClassName(data.additionalConfiguration, "pt-15 pb-2.5 lg:pt-20 lg:pb-10");
    const observedElementRef = useRef(null);
    const prevAutoplay = useRef(shouldAutoplay);

    // Set slider navigation on initialization
    useEffect(() => {
        setSliderTabNavigation(sliderTab);
        setSliderContentNavigation(sliderContent);
        setSliderInitialized(true);
    }, [sliderTab, sliderContent]);

    // Set total slides based on card items length
    useEffect(() => {
        setTotalSlides(data.cardItems.length);
    }, [data.cardItems]);

    // Enable slider autoplay
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                const [entry] = entries;
                setShouldAutoplay(entry.isIntersecting);
            },
            { threshold: 0.1 }
        );

        if (observedElementRef.current) {
            observer.observe(observedElementRef.current);
        }
        return () => {
            if (observedElementRef.current) {
                observer.unobserve(observedElementRef.current);
            }
        };
    }, []);

    // Update slider key
    useEffect(() => {
        if (prevAutoplay.current === false && shouldAutoplay === true) {
            setSliderKey((prevKey) => prevKey + 1);
        }
        prevAutoplay.current = shouldAutoplay;
    }, [shouldAutoplay]);

    // Slider tab settings
    const settingsSliderTab = {
        slidesToShow: totalSlides,
        slidesToScroll: 1,
        arrows: false,
        centerMode: false,
        swipeToSlide: true,
        centerPadding: 0,
        infinite: true,
        variableWidth: true,
        focusOnSelect: true,
        autoplaySpeed: 6000,
        responsive: [
            {
                breakpoint: 768,
                settings: {
                    slidesToShow: 1,
                    variableWidth: false,
                    autoplay: false,
                },
            },
        ],
    };

    // Slider content settings
    const settingsSliderContent = {
        slidesToShow: 1,
        slidesToScroll: 1,
        arrows: false,
        fade: true,
        cssEase: "linear",
        centerMode: false,
        swipeToSlide: true,
        centerPadding: 0,
        infinite: true,
        autoplay: true,
        speed: 500,
        autoplaySpeed: 6000,
        responsive: [
            {
                breakpoint: 768,
                settings: {
                    fade: false,
                    dots: true,
                    autoplay: false,
                    speed: 300,
                    infinite: false,
                },
            },
        ],
    };

    // Destructure data
    const { customId, title, subtitle, cardItems = [] } = data ?? {};

    // Render the HomeTabSlider component
    return (
        <section className={`${sectionPaddingClassName} contain-paint ${styles.homeTabSliderSection}`} id={customId}>
            <div className="container">
                <div className="row">
                    <div className="col-12">
                        {title && <h2 className="tag-1 mb-4.75 lg:mb-7.5">{title}</h2>}
                        {subtitle && <h3 className="title-2 mb-5.25 lg:mb-7.5">{subtitle}</h3>}
                        {cardItems.length > 0 && (
                            <div
                                className={`qlikTabSlider ${!shouldAutoplay ? "sliderAutoplayStopped" : ""} ${
                                    !sliderInitialized ? "sliderNotInitialized" : ""
                                }`}
                                /* Stop autoplay on click */
                                onClick={() => setShouldAutoplay(false)}
                                tabIndex={-1}
                                onKeyDown={(e) => {
                                    handleKeyboardEventBehavior(e, () => setShouldAutoplay(false), ["Space"]);
                                }}
                                ref={observedElementRef}
                            >
                                {/* Render the slider nav-tabs (its own slideshow) */}
                                <Slider
                                    key={`slider-tab-${sliderKey}`}
                                    className="qlikTabSliderTab qlikSlider !hidden md:!block"
                                    // eslint-disable-next-line react/jsx-props-no-spreading
                                    {...settingsSliderTab}
                                    asNavFor={sliderTabNavigation}
                                    ref={(slider) => setSliderContent(slider)}
                                    autoplay={shouldAutoplay}
                                >
                                    {cardItems?.map((slide) => (
                                        <button
                                            type="button"
                                            className="group slideWrapper py-3.5 px-7.5 cursor-pointer relative h-full
                          before:content before:absolute before:w-full before:h-0.75 before:bg-gray before:inset-x-0 before:bottom-0
                          after:content after:absolute after:w-0 after:h-0.75 after:bg-green after:inset-x-0 after:bottom-0
                          md:pl-0 tab-normal transition-color ease-in-out duration-300 lg:hover:text-green"
                                            key={slide.id}
                                        >
                                            {slide.title}
                                        </button>
                                    ))}
                                </Slider>
                                {/* Render the slider content tabs (its own slideshow) */}
                                <Slider
                                    key={`slider-content-${sliderKey}`}
                                    className="qlikTabSliderContent qlikSlider pt:5 mb-12.25 md:pt-15 md:mb-0"
                                    // eslint-disable-next-line react/jsx-props-no-spreading
                                    {...settingsSliderContent}
                                    asNavFor={sliderContentNavigation}
                                    ref={(slider) => setSliderTab(slider)}
                                    autoplay={shouldAutoplay}
                                >
                                    {cardItems?.map((slide) => (
                                        <div
                                            className="slideWrapper !flex flex-col h-full rounded shadow-lg md:flex-row md:rounded-none md:shadow-lg-none"
                                            key={slide.id}
                                        >
                                            {slide?.title && (
                                                <h4
                                                    className="tab-normal text-grey-light px-7.5 py-5 relative md:hidden
                      before:content before:absolute before:w-full before:h-0.75 before:bg-stone before:inset-x-0 before:bottom-0
                          after:content after:absolute after:w-0 after:h-0.75 after:bg-green after:inset-x-0 after:bottom-0"
                                                    // eslint-disable-next-line react/no-danger
                                                    dangerouslySetInnerHTML={{
                                                        __html: renderSanitizedHtmlContent(slide.title),
                                                    }}
                                                />
                                            )}
                                            {slide?.media?.[0] && (
                                                <div className="h-41.75 md:flex-[0_0_57.265%] md:h-100">
                                                    <Media
                                                        media={slide?.media?.[0]}
                                                        className="w-full h-full object-cover md:rounded-sm"
                                                    />
                                                </div>
                                            )}
                                            <div className="h-full p-7.5 max-md:flex max-md:flex-col md:flex-[0_0_42.736%] md:p-0 md:pl-10 lg:pl-25">
                                                {slide?.subtitle && (
                                                    <h3 className="tag-2 mb-3 md:mb-5 block">{slide.subtitle}</h3>
                                                )}
                                                {slide?.description?.description && (
                                                    <h4 className="title-4 text-blue">
                                                        {slide.description.description}
                                                    </h4>
                                                )}
                                                {slide?.body?.raw && <RichText data={slide} className="bodyContent" />}
                                                {slide?.cta?.map((button) => (
                                                    <Cta key={button.id} data={button} />
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </Slider>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default HomeTabSlider;
